import { AnimatePresence, motion } from 'framer-motion'
import {
  CandlestickChart,
  ChevronUp,
  KanbanSquare,
  LayoutDashboard,
  Settings,
  Sliders,
  Users,
  ShoppingBasket,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import TeamSwitcher from '@/components/dashboard/team-switcher'
import { LogoText } from '@/components/logo'
import { ScrollArea } from '@/components/ui/scroll-area'

import Link from '../link'
import styles from './sidebar.module.css'

interface MenuType {
  name: string
  path?: string
  icon?: any
  badge?: number
  isActive?: (pathname: string) => boolean
  children?: MenuType[]
}
const variants = {
  open: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
      delayChildren: 0.2,
    },
  },
  closed: {
    y: -10,
    scale: 0.5,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
}

const menus: MenuType[] = [
  {
    name: '概览',
    icon: LayoutDashboard,
    path: '/app/overview',
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/overview')
    },
  },
  {
    name: '管理中心',
    icon: KanbanSquare,
    children: [
      {
        name: '场地管理',
        path: '/app/management/appointment/venue',
      },
      {
        name: '预约管理',
        path: '/app/management/appointment/schedule',
        isActive: (pathname: string) => {
          return pathname.startsWith('/app/management/appointment/schedule')
        },
      },
      {
        name: '用户反馈',
        path: '/app/management/user-feedback',
      },
      {
        name: 'Banner 管理',
        path: '/app/management/banner',
      },
      {
        name: '充值产品管理',
        path: '/app/management/appointment/recharge-product',
      },
      {
        name: '充值订单',
        path: '/app/management/appointment/recharge-order',
      },
      {
        name: '礼品码管理',
        path: '/app/management/gift-code',
      },
    ],
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/management')
    },
  },
  {
    name: '会员中心',
    icon: Users,
    children: [
      {
        name: '会员管理',
        path: '/app/member-center/member',
      },
      {
        name: '等级设置',
        path: '/app/member-center/level',
        isActive: (pathname: string) => {
          return pathname.startsWith('/app/member-center/level')
        },
      },
      {
        name: '积分管理',
        path: '/app/member-center/points',
        isActive: (pathname: string) => {
          return pathname.startsWith('/app/member-center/points')
        },
      },
    ],
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/member-center')
    },
  },
  {
    name: '商品管理',
    icon: ShoppingBasket,
    children: [
      {
        name: '商品分类',
        path: '/app/product-center/category',
      },
      {
        name: '自营商品',
        path: '/app/product-center/self-operated',
      },
      {
        name: '商品订单',
        path: '/app/product-center/order',
      },
    ],
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/product-center')
    },
  },
  {
    name: '项目配置',
    icon: Sliders,
    children: [
      {
        name: '小程序',
        path: '/app/project/mini-app',
      },
      {
        name: '云服务',
        path: '/app/project/cloud',
      },
      {
        name: '商铺管理',
        path: '/app/project/store',
      },
      {
        name: '协作管理',
        path: '/app/project/member',
      },
      {
        name: '项目信息',
        path: '/app/project/info',
      },
    ],
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/project')
    },
  },
  {
    name: '费用中心',
    icon: CandlestickChart,
    children: [
      {
        name: '账单明细',
        path: '/app/billings/details',
      },
      {
        name: '充值记录',
        path: '/app/billings/recharge',
      },
      {
        name: '礼品卡兑换',
        path: '/app/billings/gift-card',
      },
    ],
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/billings')
    },
  },
  {
    name: '系统设置',
    icon: Settings,
    path: '/app/setting',
    isActive: (pathname: string) => {
      return pathname.startsWith('/app/setting')
    },
  },
]

const renderMenu = (menu: MenuType) => {
  const location = useLocation()
  const defaultOpenPopover = useMemo(() => {
    return (
      location.pathname === menu.path ||
      (menu.children &&
        menu.children?.filter((item) => item.path === location.pathname)
          ?.length > 0) ||
      false
    )
  }, [location, menu])
  const isActive = useMemo(() => {
    if (menu.isActive) {
      return menu.isActive(location.pathname)
    }
    return false
  }, [menu, location])
  const [openPopover, setOpenPopover] = useState<boolean>(defaultOpenPopover)
  return (
    <AnimatePresence key={`ap-${menu.path || menu.name}`}>
      <li>
        <Link
          className={`relative focus:outline-none m-0 flex min-h-[40px] w-full cursor-pointer items-center rounded px-3 font-medium transition-colors hover:text-primary hover:bg-wedges-gray-50 ${isActive ? 'bg-[#f7f7f8]' : 'text-muted-foreground'
            }`}
          href={menu.path || '#'}
          key={`link-${menu.path || menu.name}`}
          onClick={(e) => {
            if (!menu.path) {
              e.preventDefault()
            }
            setOpenPopover(!openPopover)
          }}
        >
          <div className="mr-3">
            {
              <menu.icon className="h-4 w-4 fill-transparent stroke-current text-wedges-gray-400" />
            }
          </div>
          <div className="truncate">{menu.name}</div>
          {menu.children?.length && (
            <div
              className={`ml-auto h-6 w-6 flex-shrink-0 text-wedges-gray-400 transition-all transform ${!openPopover ? 'rotate-180' : ''
                }`}
            >
              <ChevronUp className="h-full" />
            </div>
          )}
        </Link>
        {menu.children && (
          <div className={openPopover ? 'block' : 'hidden'}>
            {renderMenus(menu.children, location.pathname, openPopover)}
          </div>
        )}
      </li>
    </AnimatePresence>
  )
}

const renderMenus = (menu: MenuType[], activePath?: string, open?: boolean) => {
  return menu.map((menu) => {
    const { name, icon, path, children } = menu
    const isCurrentActive =
      activePath === path || menu.isActive?.(location.pathname)
    return (
      <Link
        key={`menu-link-${menu.path || menu.name}`}
        href={menu.path || '#'}
        onClick={(e) => {
          if (!menu.path) {
            e.preventDefault()
          }
        }}
        className={`${styles['sidebar-menu-item']
          } focus:outline-none relative m-0 flex min-h-[40px] w-full cursor-pointer items-center rounded pr-2 pl-12 transition-colors hover:text-primary hover:bg-[#f7f7f8] ${isCurrentActive
            ? styles['sidebar-menu-item-active']
            : 'text-muted-foreground'
          }`}
      >
        <motion.div
          animate={open ? 'open' : 'closed'}
          variants={variants}
          key={menu.name}
          className={'truncate'}
        >
          {menu.name}
        </motion.div>
      </Link>
    )
  })
}
export default function Sidebar() {
  return (
    <div className="sticky top-0 h-full">
      <ScrollArea className="h-full overflow-y-auto border-r border-slate-6">
        <aside className="px-4 hidden md:flex h-screen w-[250px] flex-shrink-0 flex-col justify-between">
          <div className="flex h-[60px] items-center">
            <Link href="/" className="px-3">
              <LogoText
                width={60}
                className="w-full h-full fill-black dark:fill-white"
              />
            </Link>
          </div>
          <nav className="mt-2 flex-1 pb-[100px]">
            <ul>{menus.map((menu) => renderMenu(menu))}</ul>
          </nav>
          <div className="absolute bottom-0 left-0 z-10 bg-white right-0 border-t border-slate-6 px-4 py-6">
            <div className="w-full">
              <TeamSwitcher />
            </div>
          </div>
        </aside>
      </ScrollArea>
    </div>
  )
}
